// client/src/components/teams/TeamLeaksPanel.tsx
//
// Panel de leaks del team para el coach: detecta automáticamente las stats
// fuera del benchmark para cada jugador y las muestra ordenadas por severidad.
// Feature A3 del coaching dashboard.

import React, { useMemo, useState } from 'react';
import { useT } from '../../lib/i18n';
import { DerivedStats } from '../../utils/pokerStats/types';
import { RANGES_BY_STACK } from '../stats/leakRanges';
import { useBenchmarks, BenchmarkContextValue } from '../../contexts/BenchmarkContext';
import { payloadToDerived } from './payloadToStats';

// ── Tipos internos ────────────────────────────────────────────────────────────

interface LeakItem {
  key:    string;
  label:  string;
  value:  number;
  min:    number;
  max:    number;
  status: 'critical' | 'warn';
  unit:   string;
}

interface PlayerLeakSummary {
  userId:        string | null;
  email:         string | null;
  handsCount:    number;
  derived:       DerivedStats | null;
  leaks:         LeakItem[];
  criticalCount: number;
  warnCount:     number;
}

interface PlayerRow {
  userId:       string | null;
  email:        string | null;
  handsCount:   number;
  statsPayload: Record<string, any>;
}

interface Props {
  players:      PlayerRow[];
  onViewPlayer: (userId: string | null) => void;
}

// ── Definición de stats a evaluar ────────────────────────────────────────────
//
// key:        clave en RANGES_BY_STACK (para label + rango)
// getValue:   extrae el valor numérico de DerivedStats
// hasOpp:     función que devuelve true si hay muestra suficiente
// unit:       '%' o '' (para AF que es ratio)

interface StatCheckDef {
  key:      string;
  getValue: (d: DerivedStats) => number | undefined;
  hasOpp:   (d: DerivedStats, handsCount: number) => boolean;
  unit:     string;
}

const MIN_HANDS_BASE    = 100;
const MIN_HANDS_POSTFLOP = 150;
const MIN_HANDS_ADVANCED = 200;
const MIN_OPP_SPECIFIC  = 25;
const MIN_OPP_RARE      = 20;

const STAT_CHECKS: StatCheckDef[] = [
  // ── Preflop básico ────────────────────────────────────────────────────────
  { key: 'vpip',               getValue: d => d.vpip,               hasOpp: (_, h) => h >= MIN_HANDS_BASE,    unit: '%' },
  { key: 'pfr',                getValue: d => d.pfr,                hasOpp: (_, h) => h >= MIN_HANDS_BASE,    unit: '%' },
  { key: 'openLimp',           getValue: d => d.openLimp,           hasOpp: (d)    => (d.openLimpOpp ?? 0) >= 50,         unit: '%' },
  { key: 'threebet',           getValue: d => d.threebet,           hasOpp: (_, h) => h >= MIN_HANDS_ADVANCED, unit: '%' },
  { key: 'foldTo3bet',         getValue: d => d.foldTo3bet,         hasOpp: (_, h) => h >= MIN_HANDS_BASE,    unit: '%' },
  { key: 'foldTo3betAllin',    getValue: d => d.foldTo3betAllin,    hasOpp: (d)    => d.foldTo3betAllinOpp    >= MIN_OPP_SPECIFIC, unit: '%' },
  { key: 'foldTo3betNonAllin', getValue: d => d.foldTo3betNonAllin, hasOpp: (d)    => d.foldTo3betNonAllinOpp >= MIN_OPP_SPECIFIC, unit: '%' },
  { key: 'squeeze',            getValue: d => d.squeeze,            hasOpp: (d)    => (d.squeezeOpp ?? 0) >= MIN_OPP_SPECIFIC,    unit: '%' },
  { key: 'fourbet',            getValue: d => d.fourbet,            hasOpp: (d)    => (d.fourbetOpp ?? 0) >= MIN_OPP_RARE,        unit: '%' },
  { key: 'ats',                getValue: d => d.ats,                hasOpp: (_, h) => h >= MIN_HANDS_ADVANCED, unit: '%' },

  // ── Postflop CBet ─────────────────────────────────────────────────────────
  { key: 'cbetFlop',           getValue: d => d.cbetFlop,           hasOpp: (_, h) => h >= MIN_HANDS_BASE,     unit: '%' },
  { key: 'cbetFlopSuccess',    getValue: d => d.cbetFlopSuccess,    hasOpp: (_, h) => h >= MIN_HANDS_BASE,     unit: '%' },
  { key: 'cbetTurn',           getValue: d => d.cbetTurn,           hasOpp: (_, h) => h >= MIN_HANDS_BASE,     unit: '%' },
  { key: 'cbetRiver',          getValue: d => d.cbetRiver,          hasOpp: (_, h) => h >= MIN_HANDS_ADVANCED, unit: '%' },
  { key: 'delayCbet',          getValue: d => d.delayCbet,          hasOpp: (d)    => d.delayCbetOpp >= MIN_OPP_SPECIFIC,         unit: '%' },
  { key: 'cbet3BetPotIP',      getValue: d => d.cbet3BetPotIP,      hasOpp: (d)    => d.cbet3BetPotIPOpp  >= MIN_OPP_RARE,        unit: '%' },
  { key: 'cbet3BetPotOOP',     getValue: d => d.cbet3BetPotOOP,     hasOpp: (d)    => d.cbet3BetPotOOPOpp >= MIN_OPP_RARE,        unit: '%' },

  // ── Defensa ───────────────────────────────────────────────────────────────
  { key: 'foldToCbetFlop',     getValue: d => d.foldToCbetFlop,     hasOpp: (_, h) => h >= MIN_HANDS_BASE,      unit: '%' },
  { key: 'foldToCbetTurn',     getValue: d => d.foldToCbetTurn,     hasOpp: (_, h) => h >= MIN_HANDS_POSTFLOP,  unit: '%' },
  { key: 'callCbetTurn',       getValue: d => d.callCbetTurn,       hasOpp: (_, h) => h >= MIN_HANDS_POSTFLOP,  unit: '%' },

  // ── Donk / Probe ─────────────────────────────────────────────────────────
  { key: 'donkFlop',           getValue: d => d.donkFlop,           hasOpp: (_, h) => h >= MIN_HANDS_BASE,     unit: '%' },
  { key: 'donkTurn',           getValue: d => d.donkTurn,           hasOpp: (_, h) => h >= MIN_HANDS_POSTFLOP, unit: '%' },
  { key: 'donkRiver',          getValue: d => d.donkRiver,          hasOpp: (_, h) => h >= MIN_HANDS_ADVANCED, unit: '%' },
  { key: 'probeTotal',         getValue: d => d.probeTotal,         hasOpp: (_, h) => h >= MIN_HANDS_POSTFLOP, unit: '%' },

  // ── XRaise flop ──────────────────────────────────────────────────────────
  // Nota: la clave en RANGES es 'xraiseFlop' pero en DerivedStats es xraiseFlopPct
  { key: 'xraiseFlop',         getValue: d => d.xraiseFlopPct,      hasOpp: (d)    => (d.xraiseFlopOpp ?? 0) >= MIN_OPP_SPECIFIC, unit: '%' },

  // ── Showdown ─────────────────────────────────────────────────────────────
  { key: 'wtsd',               getValue: d => d.wtsd,               hasOpp: (_, h) => h >= MIN_HANDS_ADVANCED, unit: '%' },
  { key: 'wsd',                getValue: d => d.wsd,                hasOpp: (_, h) => h >= MIN_HANDS_ADVANCED, unit: '%' },
  { key: 'wwsf',               getValue: d => d.wwsf,               hasOpp: (_, h) => h >= MIN_HANDS_ADVANCED, unit: '%' },
  { key: 'af',                 getValue: d => d.af,                 hasOpp: (_, h) => h >= MIN_HANDS_ADVANCED, unit: ''  },
];

// ── Detectar leaks de un jugador ──────────────────────────────────────────────

function detectLeaks(
  derived: DerivedStats,
  handsCount: number,
  bm: BenchmarkContextValue,
): LeakItem[] {
  const leaks: LeakItem[] = [];

  for (const check of STAT_CHECKS) {
    if (!check.hasOpp(derived, handsCount)) continue;

    const value = check.getValue(derived);
    if (value === undefined || value === null) continue;

    const range = bm.getRange(check.key, 'all') ?? RANGES_BY_STACK.all[check.key];
    if (!range) continue;

    const status = bm.evalStat(check.key, value, 'all');
    if (status === 'ok') continue;

    leaks.push({
      key:    check.key,
      label:  range.label,
      value,
      min:    range.min,
      max:    range.max,
      status,
      unit:   check.unit,
    });
  }

  // Críticos primero, luego warn; dentro de cada grupo, ordenar por desviación del rango
  return leaks.sort((a, b) => {
    if (a.status !== b.status) return a.status === 'critical' ? -1 : 1;
    const devA = a.value > a.max ? a.value - a.max : a.min - a.value;
    const devB = b.value > b.max ? b.value - b.max : b.min - b.value;
    return devB - devA;
  });
}

// ── Componente principal ─────────────────────────────────────────────────────

export function TeamLeaksPanel({ players, onViewPlayer }: Props) {
  const { t } = useT();
  const bm = useBenchmarks();
  const [filterMode, setFilterMode] = useState<'critical' | 'all'>('critical');
  const [expanded,   setExpanded]   = useState<Set<string>>(new Set());

  const summaries: PlayerLeakSummary[] = useMemo(() => {
    return players
      .map(p => {
        const derived = payloadToDerived(p.statsPayload);
        if (!derived || p.handsCount === 0) {
          return { userId: p.userId, email: p.email, handsCount: p.handsCount, derived: null, leaks: [], criticalCount: 0, warnCount: 0 };
        }
        const leaks = detectLeaks(derived, p.handsCount, bm);
        return {
          userId:        p.userId,
          email:         p.email,
          handsCount:    p.handsCount,
          derived,
          leaks,
          criticalCount: leaks.filter(l => l.status === 'critical').length,
          warnCount:     leaks.filter(l => l.status === 'warn').length,
        };
      })
      // Ordenar: más leaks críticos primero, luego por warnings
      .sort((a, b) => {
        if (b.criticalCount !== a.criticalCount) return b.criticalCount - a.criticalCount;
        return b.warnCount - a.warnCount;
      });
  }, [players, bm]);

  const hasSyncedPlayers = summaries.some(s => s.handsCount > 0);
  const playersWithCritical = summaries.filter(s => s.criticalCount > 0).length;
  const playersWithLeaks    = summaries.filter(s => s.criticalCount > 0 || s.warnCount > 0).length;

  const toggleExpand = (id: string) => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // ── Styles ────────────────────────────────────────────────────────────────

  const dotStyle = (status: 'critical' | 'warn'): React.CSSProperties => ({
    display: 'inline-block', width: '7px', height: '7px', borderRadius: '50%',
    background: status === 'critical' ? '#e05252' : '#e0a852',
    flexShrink: 0,
  });

  if (!hasSyncedPlayers) {
    return (
      <p style={{ color: 'var(--muted)', fontSize: '0.82rem' }}>
        {t('teams.leaksNoData')}
      </p>
    );
  }

  return (
    <div>
      {/* Resumen + filtro */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap', gap: '0.5rem' }}>
        <div style={{ fontSize: '0.75rem', color: 'var(--muted)' }}>
          {playersWithCritical > 0 ? (
            <span>
              <span style={{ color: '#e05252' }}>{playersWithCritical}</span>
              {' '}{t('teams.leaksSummaryPlayers')}
              {playersWithLeaks > playersWithCritical && (
                <span> · {playersWithLeaks - playersWithCritical} {t('teams.leaksSummaryWarnOnly')}</span>
              )}
            </span>
          ) : (
            <span style={{ color: '#52c2a0' }}>✓ {t('teams.leaksAllGood')}</span>
          )}
        </div>
        <div style={{ display: 'flex', gap: '0.3rem' }}>
          {(['critical', 'all'] as const).map(mode => (
            <button
              key={mode}
              onClick={() => setFilterMode(mode)}
              style={{
                padding: '0.2rem 0.65rem', border: 'none', borderRadius: '4px',
                fontSize: '0.7rem', cursor: 'pointer', fontFamily: 'var(--mono)',
                letterSpacing: '0.5px',
                background: filterMode === mode ? '#4a9ae0' : '#1e2636',
                color: filterMode === mode ? '#fff' : 'var(--muted)',
              }}
            >
              {mode === 'critical' ? t('teams.leaksFilterCritical') : t('teams.leaksFilterAll')}
            </button>
          ))}
        </div>
      </div>

      {/* Lista de jugadores */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
        {summaries.map((s, i) => {
          const key = s.userId ?? `player-${i}`;
          const isExpanded = expanded.has(key);
          const visibleLeaks = filterMode === 'critical'
            ? s.leaks.filter(l => l.status === 'critical')
            : s.leaks;

          if (filterMode === 'critical' && s.criticalCount === 0) return null;

          return (
            <div
              key={key}
              style={{
                background: '#0e1320', border: '1px solid #1e2636', borderRadius: '6px',
                overflow: 'hidden',
              }}
            >
              {/* Fila del jugador */}
              <div
                style={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  padding: '0.65rem 1rem', cursor: s.derived ? 'pointer' : 'default',
                  gap: '0.75rem', flexWrap: 'wrap',
                }}
                onClick={() => s.derived && toggleExpand(key)}
              >
                {/* Email + badges */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', minWidth: 0 }}>
                  {s.criticalCount > 0 || s.warnCount > 0 ? (
                    <span style={{
                      fontSize: '0.65rem', letterSpacing: '0.5px',
                      transform: isExpanded ? 'rotate(90deg)' : 'none',
                      transition: 'transform 0.15s', color: 'var(--muted)',
                    }}>▶</span>
                  ) : (
                    <span style={{ color: '#52c2a0', fontSize: '0.8rem' }}>✓</span>
                  )}
                  <span style={{ fontSize: '0.82rem', color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {s.email ?? '—'}
                  </span>
                  {s.handsCount < MIN_HANDS_BASE && s.handsCount > 0 && (
                    <span style={{ fontSize: '0.62rem', color: 'var(--muted)', background: '#1e2636', padding: '0.1rem 0.4rem', borderRadius: '3px' }}>
                      {t('teams.leaksLowSample')}
                    </span>
                  )}
                </div>

                {/* Badges + acción */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexShrink: 0 }}>
                  {s.criticalCount > 0 && (
                    <span style={{ fontSize: '0.68rem', color: '#e05252', background: '#e0525218', border: '1px solid #e0525240', borderRadius: '4px', padding: '0.15rem 0.5rem', whiteSpace: 'nowrap' }}>
                      {s.criticalCount} {t('teams.leaksBadgeCritical')}
                    </span>
                  )}
                  {s.warnCount > 0 && (
                    <span style={{ fontSize: '0.68rem', color: '#e0a852', background: '#e0a85218', border: '1px solid #e0a85240', borderRadius: '4px', padding: '0.15rem 0.5rem', whiteSpace: 'nowrap' }}>
                      {s.warnCount} {t('teams.leaksBadgeWarn')}
                    </span>
                  )}
                  {s.criticalCount === 0 && s.warnCount === 0 && s.derived && (
                    <span style={{ fontSize: '0.68rem', color: '#52c2a0' }}>{t('teams.leaksPlayerClean')}</span>
                  )}
                  {s.derived && (
                    <button
                      className="btn btn-secondary"
                      style={{ fontSize: '0.65rem', padding: '0.15rem 0.45rem', whiteSpace: 'nowrap' }}
                      onClick={e => { e.stopPropagation(); onViewPlayer(s.userId); }}
                    >
                      {t('teams.viewPlayerStats')}
                    </button>
                  )}
                  {s.handsCount === 0 && (
                    <span style={{ fontSize: '0.7rem', color: 'var(--muted)' }}>{t('teams.noSyncData')}</span>
                  )}
                </div>
              </div>

              {/* Leaks expandidos */}
              {isExpanded && visibleLeaks.length > 0 && (
                <div style={{ borderTop: '1px solid #1e2636', padding: '0.5rem 1rem 0.75rem' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.78rem' }}>
                    <tbody>
                      {visibleLeaks.map(leak => (
                        <tr key={leak.key} style={{ borderBottom: '1px solid #0d1117' }}>
                          <td style={{ padding: '0.35rem 0.5rem 0.35rem 0', width: '16px' }}>
                            <span style={dotStyle(leak.status)} />
                          </td>
                          <td style={{ padding: '0.35rem 0.75rem 0.35rem 0', color: 'var(--text)' }}>
                            {leak.label}
                          </td>
                          <td style={{
                            padding: '0.35rem 0.5rem', textAlign: 'right', fontWeight: 700,
                            color: leak.status === 'critical' ? '#e05252' : '#e0a852',
                          }}>
                            {leak.unit === '' ? leak.value : `${leak.value}%`}
                          </td>
                          <td style={{ padding: '0.35rem 0 0.35rem 0.5rem', textAlign: 'right', color: 'var(--muted)', fontSize: '0.7rem', whiteSpace: 'nowrap' }}>
                            {t('teams.leaksRange')} {leak.min}{leak.unit}–{leak.max}{leak.unit}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Sin leaks en modo "críticos" → no mostrar expanded */}
              {isExpanded && visibleLeaks.length === 0 && filterMode === 'critical' && (
                <div style={{ borderTop: '1px solid #1e2636', padding: '0.5rem 1rem', fontSize: '0.75rem', color: '#52c2a0' }}>
                  ✓ {t('teams.leaksPlayerClean')}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
